import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { updateUser, useSession } from '../../lib/auth-client';
import { trpc } from '../../lib/trpc';
import { Panel } from '../common/Panel';
import { CardSkeleton } from '../common/Skeleton';

export function AccountSettings() {
  const { data: session, isPending: sessionLoading } = useSession();
  const utils = trpc.useUtils();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session?.user) {
      setName(session.user.name);
    }
  }, [session?.user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setSuccess(false);
    setError(null);
    try {
      const result = await updateUser({ name: name.trim() });
      if (result.error) {
        setError(result.error.message ?? 'Kunde inte uppdatera namn.');
      } else {
        setSuccess(true);
        await utils.invalidate();
      }
    } catch {
      setError('Kunde inte uppdatera namn.');
    } finally {
      setSaving(false);
    }
  };

  if (sessionLoading) {
    return <CardSkeleton lines={3} />;
  }

  const hasChanged = name.trim() !== (session?.user?.name ?? '');

  return (
    <div className="space-y-6">
      <Panel title="Profil">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="account-name" className="text-muted-foreground">
              Namn
            </Label>
            <Input
              id="account-name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="account-email" className="text-muted-foreground">
              E-post
            </Label>
            <Input
              id="account-email"
              value={session?.user?.email ?? ''}
              disabled
              className="bg-muted text-muted-foreground"
            />
            <p className="text-xs text-muted-foreground/70">E-postadressen kan inte ändras.</p>
          </div>
          <Button
            type="submit"
            disabled={saving || !hasChanged || !name.trim()}
            className="w-full sm:w-auto"
          >
            {saving ? 'Sparar…' : 'Spara'}
          </Button>
          {success && (
            <p role="status" className="text-positive text-sm">
              Namn uppdaterat!
            </p>
          )}
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
        </form>
      </Panel>
    </div>
  );
}
